/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RECAPTCHA_ACTIONS } from '../constants/recaptcha';
import { AccessTokenResponse, VerifyEmailOtpRequest } from '../managers';
import {
  handleLoginError,
  handleSuccessfulLogin,
  useApiKey,
  useRecaptchaManager,
  useSsoApiService,
} from './account';

export const useVerifyEmailLogin = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const recaptchaManager = useRecaptchaManager();
  const apiKeyQuery = useApiKey();

  return useMutation({
    mutationFn: async ({
      username,
      otp,
      status,
    }: VerifyEmailOtpRequest): Promise<AccessTokenResponse> => {
      const clientKey = await apiKeyQuery.getToken();
      if (!clientKey?.apiKey) throw new Error('Missing API keys');
      if (!clientKey?.id) throw new Error('Missing Client ID');

      return api.verifyPasswordlessEmailOtp(
        { username, otp, ...(status ? { status } : {}) },
        clientKey.id,
        clientKey.apiKey,
        await recaptchaManager.execute(RECAPTCHA_ACTIONS.LOGIN),
      );
    },

    onSuccess: async (data) => {
      await handleSuccessfulLogin(queryClient, data);
    },

    onError: async (error) => {
      await handleLoginError(queryClient, error, 'Credentials');
    },
  });
};
